import React, { useState, useEffect, useRef, useMemo } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  LayoutDashboard,
  Ticket,
  Users,
  DollarSign,
  Settings,
  LogOut,
  Plus,
  Trash2,
  Check,
  Clock,
  ChevronDown,
  Search,
  X,
  QrCode,
  Camera,
  CheckCircle,
  XCircle,
  AlertTriangle,
  RefreshCw,
  Edit,
  Download,
  FileSpreadsheet,
  FileText,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Tag,
  FormInput,
  Calendar,
  Mail,
  Send,
  Eye,
  Menu,
  Paperclip,
  Wrench,
} from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Switch } from "../../components/ui/switch";
import { useAuth } from "../../context/AuthContext";
import { Html5Qrcode } from "html5-qrcode";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { SessionManagement, CouponManagement, CustomFieldsManagement } from "../../components/AdminSettings";
import RichTextEditor from "../../components/RichTextEditor";

import { API_URL } from "../../config";
import { exportToPDFMulti, exportToExcelMulti } from "../../lib/export";
import { ColumnPickerModal } from "../../components/admin/ColumnPickerModal";

export const FinancialSummary = () => {
  const [stats, setStats] = useState(null);
  const [sales, setSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPdfPicker, setShowPdfPicker] = useState(false);
  const [showColsPicker, setShowColsPicker] = useState(false);
  const FIN_DEFAULT_COLS = [
    "tt_tipo","tt_total","tt_paid","tt_free","tt_pending","tt_revenue","tt_percent",
    "ses_date","ses_time","ses_total","ses_available","ses_sold","ses_revenue",
  ];
  const FIN_COLS_STORAGE = "financial-table-cols-v1";
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const raw = localStorage.getItem(FIN_COLS_STORAGE);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length) return new Set(parsed);
      }
    } catch {}
    return new Set(FIN_DEFAULT_COLS);
  });
  const colVisible = (k) => visibleCols.has(k);
  useEffect(() => {
    try { localStorage.setItem(FIN_COLS_STORAGE, JSON.stringify(Array.from(visibleCols))); } catch {}
  }, [visibleCols]);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const [statsRes, salesRes] = await Promise.all([
        axios.get(`${API_URL}/api/admin/financial`, { withCredentials: true }),
        axios.get(`${API_URL}/api/admin/sales`, { withCredentials: true }),
      ]);
      setStats(statsRes.data);
      setSales(salesRes.data || []);
    } catch (error) {
      console.error("Error fetching stats:", error);
    } finally {
      setLoading(false);
    }
  };

  // Build "Vendas por Tipo de Bilhete" rows from raw sales — same shape as Dashboard.
  const ticketTypeBreakdown = useMemo(() => {
    const breakdown = new Map();
    sales.forEach((s) => {
      const key = s.ticket_type_name || "(sem tipo)";
      const acc = breakdown.get(key) || { name: key, total: 0, paid: 0, free: 0, pending: 0, revenue: 0 };
      acc.total += 1;
      if (s.payment_status === "paid") {
        const price = Number(s.final_price || 0);
        if (price > 0) {
          acc.paid += 1;
          acc.revenue += price;
        } else {
          acc.free += 1;
        }
      } else if (s.payment_status === "pending") {
        acc.pending += 1;
      }
      breakdown.set(key, acc);
    });
    return Array.from(breakdown.values()).sort((a, b) => b.total - a.total);
  }, [sales]);

  const ticketTypeTotals = useMemo(() => {
    return ticketTypeBreakdown.reduce(
      (a, r) => ({
        total: a.total + r.total,
        paid: a.paid + r.paid,
        free: a.free + r.free,
        pending: a.pending + r.pending,
        revenue: a.revenue + r.revenue,
      }),
      { total: 0, paid: 0, free: 0, pending: 0, revenue: 0 }
    );
  }, [ticketTypeBreakdown]);

  const exportFinancialData = () => {
    if (!stats?.session_breakdown) return [];
    return stats.session_breakdown.map((s) => ({
      Data: s.date,
      Horário: s.time,
      "Lugares Totais": s.total_seats,
      "Disponíveis": s.available_seats,
      "Vendidos": s.sold,
      "Receita (€)": s.revenue?.toFixed(2),
    }));
  };

  // Per-ticket-type rows formatted for export.
  const exportTicketTypeData = () => {
    if (!ticketTypeBreakdown.length) return [];
    const rows = ticketTypeBreakdown.map((r) => ({
      Tipo: r.name,
      Total: r.total,
      Pagos: r.paid,
      Gratuitos: r.free,
      Pendentes: r.pending,
      "Receita (€)": r.revenue.toFixed(2),
      "% do Total": ticketTypeTotals.total > 0 ? `${((r.total / ticketTypeTotals.total) * 100).toFixed(0)}%` : "—",
    }));
    rows.push({
      Tipo: "TOTAL",
      Total: ticketTypeTotals.total,
      Pagos: ticketTypeTotals.paid,
      Gratuitos: ticketTypeTotals.free,
      Pendentes: ticketTypeTotals.pending,
      "Receita (€)": ticketTypeTotals.revenue.toFixed(2),
      "% do Total": "100%",
    });
    return rows;
  };

  const ticketTypeColumns = [
    { key: "Tipo", header: "Tipo" },
    { key: "Total", header: "Total" },
    { key: "Pagos", header: "Pagos" },
    { key: "Gratuitos", header: "Gratuitos" },
    { key: "Pendentes", header: "Pendentes" },
    { key: "Receita (€)", header: "Receita (€)" },
    { key: "% do Total", header: "% do Total" },
  ];

  const financialColumns = [
    { key: "Data", header: "Data" },
    { key: "Horário", header: "Horário" },
    { key: "Lugares Totais", header: "Lugares Totais" },
    { key: "Disponíveis", header: "Disponíveis" },
    { key: "Vendidos", header: "Vendidos" },
    { key: "Receita (€)", header: "Receita (€)" },
  ];

  const totalPaidCount = useMemo(
    () => sales.filter((s) => s.payment_status === "paid").length,
    [sales],
  );
  const totalPaidRevenue = useMemo(
    () => sales
      .filter((s) => s.payment_status === "paid")
      .reduce((acc, s) => acc + (Number(s.final_price ?? s.total_amount) || 0), 0),
    [sales],
  );

  if (loading) {
    return <div className="animate-pulse">A carregar...</div>;
  }

  return (
    <div data-testid="financial-summary">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex items-center gap-3">
          <Ticket className="w-6 h-6 text-violet-400" />
          <div>
            <h3 className="text-lg font-semibold text-white">Vendas (Bilhetes)</h3>
            <p className="text-zinc-500 text-sm">
              {totalPaidCount} bilhete(s) pago(s) · Receita <span className="text-violet-300">{totalPaidRevenue.toFixed(2)}€</span> · Breakdown por Tipo de Bilhete e por Sessão (agregado, não duplicado no Saldo)
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setShowColsPicker(true)} className="btn-secondary" data-testid="open-cols-picker-financial" title="Escolher colunas visíveis">
            <Eye className="w-4 h-4 mr-2" />
            Colunas
          </Button>
          <Button onClick={() => setShowPdfPicker(true)} className="btn-secondary" data-testid="export-financial-pdf">
            <FileText className="w-4 h-4 mr-2" />
            PDF
          </Button>
          <Button
            onClick={() => exportToExcelMulti([
              { name: "Por Tipo de Bilhete", data: exportTicketTypeData() },
              { name: "Por Sessão", data: exportFinancialData() },
            ], "financeiro")}
            className="btn-secondary"
            data-testid="export-financial-excel"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
        </div>
      </div>

      {/* PDF Column Picker (sessions table only; ticket-type table appended automatically below) */}
      <ColumnPickerModal
        open={showPdfPicker}
        onClose={() => setShowPdfPicker(false)}
        columns={financialColumns}
        storageKey="pdf-cols-financeiro"
        title="Exportar PDF · Finanças"
        onConfirm={(cols) => exportToPDFMulti([
          { title: "Vendas por Tipo de Bilhete", columns: ticketTypeColumns, data: exportTicketTypeData() },
          { title: "Detalhes por Sessão", columns: cols, data: exportFinancialData() },
        ], "Resumo Financeiro", "financeiro")}
      />

      {/* Table Column Visibility Picker */}
      <ColumnPickerModal
        open={showColsPicker}
        onClose={() => setShowColsPicker(false)}
        columns={[
          { key: "tt_tipo", header: "Tipo Bilhete · Tipo" },
          { key: "tt_total", header: "Tipo Bilhete · Total" },
          { key: "tt_paid", header: "Tipo Bilhete · Pagos" },
          { key: "tt_free", header: "Tipo Bilhete · Gratuitos" },
          { key: "tt_pending", header: "Tipo Bilhete · Pendentes" },
          { key: "tt_revenue", header: "Tipo Bilhete · Receita" },
          { key: "tt_percent", header: "Tipo Bilhete · % do Total" },
          { key: "ses_date", header: "Sessão · Data" },
          { key: "ses_time", header: "Sessão · Horário" },
          { key: "ses_total", header: "Sessão · Lugares Totais" },
          { key: "ses_available", header: "Sessão · Disponíveis" },
          { key: "ses_sold", header: "Sessão · Vendidos" },
          { key: "ses_revenue", header: "Sessão · Receita" },
        ]}
        storageKey={FIN_COLS_STORAGE}
        title="Colunas visíveis · Finanças"
        confirmLabel="Aplicar"
        onConfirm={(cols) => { setVisibleCols(new Set(cols.map((c) => c.key))); setShowColsPicker(false); }}
      />

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-8">
        <div className="bg-violet-500/10 border border-violet-500/30 rounded-lg px-3 py-2">
          <p className="text-violet-300 text-[10px] uppercase tracking-wider font-semibold">Vendas (Bilhetes)</p>
          <p className="text-white font-mono text-sm">{(totalPaidRevenue || 0).toFixed(2)}€</p>
          <p className="text-[10px] text-zinc-500 mt-0.5">Agregado (não duplicado)</p>
        </div>
        <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-lg px-3 py-2">
          <p className="text-indigo-300 text-[10px] uppercase tracking-wider font-semibold">Recebido via Stripe</p>
          <p className="text-white font-mono text-sm">{(stats?.revenue_stripe || 0).toFixed(2)}€</p>
          <p className="text-[10px] text-zinc-500 mt-0.5">Online (card, MB Way, MB, Link)</p>
        </div>
        <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2">
          <p className="text-emerald-300 text-[10px] uppercase tracking-wider font-semibold">Recebido Offline</p>
          <p className="text-white font-mono text-sm">{(stats?.revenue_offline || 0).toFixed(2)}€</p>
          <p className="text-[10px] text-zinc-500 mt-0.5">Dinheiro, transferência, cheque…</p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg px-3 py-2">
          <p className="text-blue-300 text-[10px] uppercase tracking-wider font-semibold">Receita Total Confirmada</p>
          <p className="text-white font-mono text-sm">{(stats?.total_revenue || 0).toFixed(2)}€</p>
        </div>
        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg px-3 py-2">
          <p className="text-yellow-300 text-[10px] uppercase tracking-wider font-semibold">Receita Pendente</p>
          <p className="text-white font-mono text-sm">{(stats?.pending_revenue || 0).toFixed(2)}€</p>
        </div>
        <div className="bg-white/5 border border-white/10 rounded-lg px-3 py-2">
          <p className="text-zinc-300 text-[10px] uppercase tracking-wider font-semibold">Preço Médio por Bilhete</p>
          <p className="text-white font-mono text-sm">{(stats?.price_per_ticket || 0).toFixed(2)}€</p>
        </div>
      </div>

      {ticketTypeBreakdown.length > 0 && (
        <div className="mb-8" data-testid="finance-ticket-type-breakdown">
          <h3 className="text-lg font-semibold text-white mb-4">Vendas por Tipo de Bilhete</h3>
          <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-x-auto">
            <table className="w-full text-sm min-w-[680px]">
              <thead className="bg-zinc-950 text-zinc-400">
                <tr>
                  {colVisible("tt_tipo") && <th className="text-left px-4 py-3 font-medium">Tipo</th>}
                  {colVisible("tt_total") && <th className="text-right px-4 py-3 font-medium">Total</th>}
                  {colVisible("tt_paid") && <th className="text-right px-4 py-3 font-medium">Pagos</th>}
                  {colVisible("tt_free") && <th className="text-right px-4 py-3 font-medium">Gratuitos</th>}
                  {colVisible("tt_pending") && <th className="text-right px-4 py-3 font-medium">Pendentes</th>}
                  {colVisible("tt_revenue") && <th className="text-right px-4 py-3 font-medium">Receita</th>}
                  {colVisible("tt_percent") && <th className="text-right px-4 py-3 font-medium">% do Total</th>}
                </tr>
              </thead>
              <tbody>
                {ticketTypeBreakdown.map((r) => (
                  <tr key={r.name} className="border-t border-white/5">
                    {colVisible("tt_tipo") && <td className="px-4 py-3">
                      <span className="text-xs px-2 py-1 rounded-full bg-cyan-500/10 text-cyan-300 border border-cyan-500/20">
                        {r.name}
                      </span>
                    </td>}
                    {colVisible("tt_total") && <td className="px-4 py-3 text-right text-blue-400 font-medium">{r.total}</td>}
                    {colVisible("tt_paid") && <td className="px-4 py-3 text-right text-green-400 font-medium">{r.paid}</td>}
                    {colVisible("tt_free") && <td className="px-4 py-3 text-right text-pink-400">{r.free || "—"}</td>}
                    {colVisible("tt_pending") && <td className="px-4 py-3 text-right text-yellow-400">{r.pending || "—"}</td>}
                    {colVisible("tt_revenue") && <td className="px-4 py-3 text-right text-purple-400 font-medium">{r.revenue.toFixed(2)}€</td>}
                    {colVisible("tt_percent") && <td className="px-4 py-3 text-right text-zinc-400">
                      {ticketTypeTotals.total > 0 ? `${((r.total / ticketTypeTotals.total) * 100).toFixed(0)}%` : "—"}
                    </td>}
                  </tr>
                ))}
                <tr className="border-t border-white/10 bg-zinc-950/50">
                  {colVisible("tt_tipo") && <td className="px-4 py-3 text-zinc-400 uppercase text-xs font-semibold">Total</td>}
                  {colVisible("tt_total") && <td className="px-4 py-3 text-right text-blue-400 font-bold">{ticketTypeTotals.total}</td>}
                  {colVisible("tt_paid") && <td className="px-4 py-3 text-right text-green-400 font-bold">{ticketTypeTotals.paid}</td>}
                  {colVisible("tt_free") && <td className="px-4 py-3 text-right text-pink-400 font-bold">{ticketTypeTotals.free || "—"}</td>}
                  {colVisible("tt_pending") && <td className="px-4 py-3 text-right text-yellow-400 font-bold">{ticketTypeTotals.pending || "—"}</td>}
                  {colVisible("tt_revenue") && <td className="px-4 py-3 text-right text-purple-400 font-bold">{ticketTypeTotals.revenue.toFixed(2)}€</td>}
                  {colVisible("tt_percent") && <td className="px-4 py-3 text-right text-zinc-500">100%</td>}
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}

      <h3 className="text-lg font-semibold text-white mb-4">Detalhes por Sessão</h3>
      <div className="bg-zinc-900 rounded-xl border border-white/10 overflow-x-auto">
        <table className="table-dark min-w-[700px]">
          <thead>
            <tr>
              {colVisible("ses_date") && <th>Data</th>}
              {colVisible("ses_time") && <th>Horário</th>}
              {colVisible("ses_total") && <th>Lugares Totais</th>}
              {colVisible("ses_available") && <th>Disponíveis</th>}
              {colVisible("ses_sold") && <th>Vendidos</th>}
              {colVisible("ses_revenue") && <th>Receita</th>}
            </tr>
          </thead>
          <tbody>
            {stats?.session_breakdown?.map((session) => (
              <tr key={session.session_id}>
                {colVisible("ses_date") && <td className="text-white">{session.date}</td>}
                {colVisible("ses_time") && <td className="text-zinc-400">{session.time}</td>}
                {colVisible("ses_total") && <td className="text-zinc-400">{session.total_seats}</td>}
                {colVisible("ses_available") && <td className="text-zinc-400">{session.available_seats}</td>}
                {colVisible("ses_sold") && <td className="text-green-500 font-medium">{session.sold}</td>}
                {colVisible("ses_revenue") && <td className="text-blue-500 font-medium">{session.revenue?.toFixed(2)}€</td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Settings
